'use client'

import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Accordion, Card, CardColors, Icon, Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

export enum PaystubAccordionContent {
  scorp,
  sprop,
  spouse,
}

const CheckListItem = ({ text }: { text: string }) => (
  <div className="flex items-center">
    <Icon icon={light('check')} color="green" className="mr-5" />
    <Text>{text}</Text>
  </div>
)

export const PaystubVsEstimateAccordion = ({
  content,
}: {
  content: PaystubAccordionContent
}) => (
  <Accordion
    variant="text"
    type="single"
    items={[
      {
        title: 'How should I choose?',
        content: (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Card */}
            <Card color={CardColors.LEVEL_0} className="flex-1 h-full">
              <div className="flex flex-col items-center gap-4">
                <img
                  src="https://heard-images.s3.amazonaws.com/assets/green_check.svg"
                  alt="Green check"
                  className="h-[100px] w-[100px]"
                />
                <Text
                  variant={TypographyStyles.DISPLAY_SM}
                  className="text-center"
                >
                  Year-to-date from paystub
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-center"
                >
                  {content === PaystubAccordionContent.spouse
                    ? 'How much your spouse has made so far this year'
                    : 'How much you’ve made so far this year'}
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  {content === PaystubAccordionContent.spouse
                    ? 'Choose this if your spouse:'
                    : 'Choose this if you:'}
                </Text>

                <div className="space-y-2 w-full">
                  {content === PaystubAccordionContent.scorp && (
                    <>
                      <CheckListItem text="Have received payroll payments from your S Corporation for the entire year" />
                      <CheckListItem text="Don’t expect the amount you pay yourself via payroll to change this year" />
                    </>
                  )}
                  {content === PaystubAccordionContent.sprop && (
                    <>
                      <CheckListItem text="Have a job that doesn’t vary in pay throughout the year" />
                      <CheckListItem text="Don’t expect your gross income to significantly change this year" />
                    </>
                  )}
                  {content === PaystubAccordionContent.spouse && (
                    <>
                      <CheckListItem text="Has a job that doesn’t vary in pay throughout the year" />
                      <CheckListItem text="Doesn’t expect their gross income to significantly change this year" />
                    </>
                  )}
                </div>

                <Link
                  href="https://heard-images.s3.amazonaws.com/assets/example_paystub.svg"
                  newPage
                  variant="secondary"
                >
                  View example paystub
                </Link>
              </div>
            </Card>

            {/* Right Card */}
            <Card color={CardColors.LEVEL_0} className="flex-1 h-full">
              <div className="flex flex-col items-center gap-4">
                <img
                  src="https://heard-images.s3.amazonaws.com/assets/green_calendar.svg"
                  alt="Green calendar"
                  className="h-[100px] w-[100px]"
                />
                <Text
                  variant={TypographyStyles.DISPLAY_SM}
                  className="text-center"
                >
                  Estimated income
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  {content === PaystubAccordionContent.spouse
                    ? 'How much you estimate your spouse will make for the year'
                    : 'How much you estimate you’ll make for the year'}
                </Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  {content === PaystubAccordionContent.spouse
                    ? 'Choose this if your spouse:'
                    : 'Choose this if you:'}
                </Text>

                <div className="space-y-2 w-full">
                  {content === PaystubAccordionContent.scorp && (
                    <>
                      <CheckListItem text="Earn income that is variable throughout the year (e.g. hourly income where hours are variable, frequent commissions or bonuses)" />
                      <CheckListItem text="Had or expect changes to the amount you pay yourself from your S Corporation this year (e.g. significant raise, parental or other leave)" />
                      <CheckListItem text="Started paying yourself via W-2 partially through the year" />
                    </>
                  )}
                  {content === PaystubAccordionContent.sprop && (
                    <>
                      <CheckListItem text="Earn income that is variable throughout the year (e.g. hourly income where hours are variable, frequent commissions or bonuses)" />
                      <CheckListItem text="Had or expect changes to your gross income this year (e.g. significant raise, unpaid parental leave)" />
                      <CheckListItem text="Started a new job, or ended a job partially through the year" />
                    </>
                  )}
                  {content === PaystubAccordionContent.spouse && (
                    <>
                      <CheckListItem text="Earns income that is variable throughout the year (e.g. hourly income where hours are variable, frequent commissions or bonuses)" />
                      <CheckListItem text="Has or expects changes to their gross income this year (e.g. significant raise, unpaid parental leave)" />
                      <CheckListItem text="Started a new job, or ended a job partially through the year" />
                    </>
                  )}
                </div>
              </div>
            </Card>
          </div>
        ),
      },
    ]}
  />
)
